export interface Facility {
  address: string;
  city: string;
  countryRegion: string | null;
  country_region_id: number;
  county: string;
  createdFrom: string | null;
  created_from_id: number;
  department: string | null;
  department_id: number;
  description: string | null;
  device_use_time_model_collection: boolean;
  facilityProviderFacility: string | null;
  facilityType: string | null;
  facility_provider_facility_id: number | null;
  facility_type_id: number;
  id: number;
  image_url: string | null;
  latitude: string | null;
  longitude: string | null;
  name: string;
  organization: string | null;
  organization_id: number;
  parentFacility: string | null;
  parent_facility_id: number;
  postal_code: string;
  state: string;
  target_use_count: number | null;
  timeModelCollection: number | null;
  time_model_collection_id: number;
};

export interface Detection {
  id: number;
  startTime: string;
  endTime: string;
  day: string;
  weekNumber?: number;
  monthNumber?: number;
  timePeriod: string;
  totalCount?: number;
  minCount?: number;
  maxCount?: number;
  averageCount?: number;
  usagePercentage?: number;
  booked?: boolean;
  imageUrls: DetectionImage[];
  bucketComplete?: boolean;
  count?: number;
  manualDetectionDtos: manualDetectionDto[];
};

export interface ManualDetectionSummarySet {
  facilityId: number;
  manualDetections: Detection[];
}


export interface manualDetectionDto {
  count?: number;
  count_time?: Date;
  department_id?: number;
  facility_id?: number;
  id?: number;
  organization_id?: number;
};

export interface DetectionImage {
  imageCaptureTime: string;
  imageUrl: string;
  organizationId: number;
};

export enum LoadingState {
  INIT = 'INIT',
  LOADING = 'LOADING',
  LOADED = 'LOADED',
  FAILED = 'FAILED',
}

export interface DeviceType {
  description: string;
  hardware_provider: string;
  id: number;
  name: string;
  revision_no: string;
  version_no: string;
};

export interface DeviceState {
  description: string;
  id: number;
  name: string;
};

export interface DeviceHistoryType {
  description: string;
  id: number;
  name: string;
};

export interface OrganizationType {
  description: string | null;
  id: number;
  name: string;
};

export interface TimeType {
  description: string;
  id: number;
  name: string;
};

// fixed enum values
export enum TimeTypeEnum {
  Primary = 1,
  Secondary = 2,
  School = 3,
  Service = 4,
  Closed = 5,
}

export interface BookingProvider {
  description: string | null;
  id: number;
  name: string;
  can_get_activity_types: boolean;
  can_get_associations: boolean;
  can_get_locations: boolean;
  get_bookings_requires_location_ids: boolean;
};

export interface AreaType {
  description: string;
  id: number;
  name: string;
};

export interface TimeZone {
  name: string;
};

export interface CreatedFrom {
  description: string;
  id: number;
  name: string;
};

export interface CreatedFromType {
  id: number;
  name: string;
};

export interface DepartmentType {
  description: string;
  id: number;
  name: string;
};

export interface CountryRegion {
  id: number;
  ISO_name: string;
  name: string;
};

export interface LicensePackage {
  id: number;
  name: string;
  description: string;
};

export interface LicenseTransaction {
  id?: number;
  license_package_id: number;
  license_package_name?: string;
  organization_id: number;
  organization_name?: string;
  start_date: string;
  end_date: string;
  created_at?: string;
};

export interface AdditionalLicenseTransactionFeature {
  id: number;
  organization_id: number;
  organization_name?: string;
  license_feature_id: number;
  license_feature_name?: string;
  amount_of_license_feature: number;
  delta_license_feature: number;
  active: boolean;
  start_date: string;
  alternative_end_date?: string;
  created_at: string;
};

export interface LicenseFeature {
  id: number;
  name: string;
  description: string;
};

export interface RolePackage {
  id: number;
  name: string;
  description: string | null;
  loadingState?: LoadingState;
};

export interface MasterActivity {
  country_region_id: number;
  description: string | null;
  id: number;
  name: string;
  parent_id: number | null;
};

export interface FacilityType {
  id: number;
  name: string;
  description?: string;
};

export interface FacilityProvider {
  id: number;
  name: string;
  description: string;
  country_region_id: number;
};

export enum FacilityProviderEnum {
  FacilitetsDatabasen = 1,
}

export interface ServiceResult {
  Success: boolean;
  StatusMessage?: string;
  ErrorMessage?: string;
};

export interface Organization {
  id: number;
  name: string;
  organization_type_id: number;
  organization_type_name: string;
  country_region_id: number;
  country_name: string;
};


export interface CognitoToken {
  auth_time: number;
  at_hash: string;
  aud: string;
  authorized_roles: string;
  authorized_routes: string;
  country_region_id: string;
  country_region_name: string;
  'custom:language':string;
  'cognito:username': string;
  disabled_ui: string;
  email: string;
  event_id: string;
  exp: number;
  hidden_ui: string;
  iat: number;
  iss: string;
  jti: string;
  license_package_id: string;
  license_package_name: string;
  name: string;
  organization_id: string;
  organization_name: string;
  sub: string;
  token_use: string;
  user_language: string;
  is_system_admin: boolean;
};

export type TokenUser = CognitoToken & {
  isHallMonitorUser: boolean;
  nameWithRole: string;
};

export interface JobRateUnit {
  id: number;
  name: string;
  description?: string;
};

export interface DataProvider {
  id: number;
  name: string;
};

export enum LicensePackageEnum {
  Standard = 1,
  Advanced = 2,
  Enterprise = 3
}

export enum LicenseFeatureEnum {
  RolePackage_Admin = 1000,
  RolePackage_User = 1001,
  RolePackage_Insight_User = 1002,
  Association_Users = 1003,

  Integration = 2000,

  Device = 3000,
  External_Device = 3001,

  Facility = 4000,

  Images_Per_Hour_4 = 5000,
  Images_Per_Hour_8 = 5001,
  Images_Per_Hour_12 = 5002,

  Areas = 6000,

  AreaGroups = 7000,

  Department = 8000,

  Insights = 9000,
  Insights_Full = 9001,
  Insights_Sessions = 9010,

  API = 10000
}

export interface ProductionTest {
  id: number;
  deviceId: number;
  serialNo: string;
  startTime: string;
  endTime: string;
};

export interface ProductionTestLog {
  id: number;
  deviceProductionTestId: number;
  logTime: string | undefined;
  networkOperator: string;
  signalStrength: signalStrength;
  modemTemperature: number;
  cpuUtilization: number;
  ramUsage: number;
  cpuTemperature: number;
  lastDetectionTime: string | undefined;
  signalStrengthString:string;
};

export interface signalStrength {
  RSSI: number;
  RSRP: number;
  SINR: number;
  RSRQ: number;
};
